<!--
Calendar
A component that gets an API of dates and creates a mini cal out of it.

Can navigate via links (forces a reload of the page) or a search query (uses ajax)
Can be set to navigate via months, where each day links to an anchor point (yyyy-mm-dd), or by days, where each day links to a new page/query

Can send events to vuex for integration with other components
:nav-mode: links || ajax
:date-mode: months || days
:link-mode: hash || url
-->

<template>
	<div class="c-calendar" :class="{ 'is-loading': loading }">
		<div class="c-calendar-nav">
			<h3 class="c-calendar-nav__title">
				{{ displayMonth }} {{ displayYear }}
			</h3>
			<button
				class="c-calendar-nav__item c-calendar-nav__item--prev"
				@click.prevent="prevMonth"
				:class="{ 'is-inactive': isCurrentMonth }"
			>
				<span class="h-accessibility">Previous Month</span>
				<span class="i-chevron icon">
					<chevronLeft />
				</span>
			</button>
			<button
				class="c-calendar-nav__item c-calendar-nav__item--next"
				@click.prevent="nextMonth"
			>
				<span class="h-accessibility">Next Month</span>
				<span class="i-chevron icon">
					<chevronRight />
				</span>
			</button>
		</div>
		<table class="c-calendar__table">
			<caption class="h-accessibility">
				Calendar Navigation Table
			</caption>
			<thead class="c-calendar__head">
				<tr>
					<th scope="row" class="h-accessibility">Days</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>Mo<span class="h-accessibility">nday</span></span
						>
					</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>Tu<span class="h-accessibility">esday</span></span
						>
					</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>We<span class="h-accessibility">dnesday</span></span
						>
					</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>Th<span class="h-accessibility">ursday</span></span
						>
					</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>Fr<span class="h-accessibility">iday</span></span
						>
					</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>Sa<span class="h-accessibility">turday</span></span
						>
					</th>
					<th class="c-calendar__cell" scope="col">
						<span class="c-calendar__item c-calendar__item--day"
							>Su<span class="h-accessibility">nday</span></span
						>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="week in weeks">
					<th scope="row" class="h-accessibility">Week</th>
					<td class="c-calendar__cell" v-for="day in week">
						<template v-if="day.date">
							<a
								@click="closeMiniCal()"
								data-no-swup
								class="c-calendar__item c-calendar__link"
								v-if="day.link"
								:href="hashOrLink(day)"
								:class="{
									'is-active': activeDay(day),
									'is-today': isToday(day),
								}"
								:aria-label="dateLabel(day)"
								:aria-current="isActiveDay(day) ? 'true' : null"
								>{{ day.date }}</a
							>
							<span
								class="c-calendar__item"
								v-else
								:class="{
									'is-today': isToday(day),
									'c-calendar__item--dec': day.notCurrentMonth,
									'c-calendar__item--future': futureDate(day),
								}"
								:aria-label="dateLabel(day)"
								:aria-current="isActiveDay(day) ? 'true' : null"
								>{{ day.date }}</span
							>
						</template>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
	import axios from "axios";
	const CancelToken = axios.CancelToken;
	let cancel;

	import chevronRight from "/web/static/images/svgs/base-chevron-right.svg?component";
	import chevronLeft from "/web/static/images/svgs/base-chevron-left.svg?component";

	export default {
		name: "calendar",
		data() {
			return {
				loading: true,
				meta: {},
				weeks: [],
				newWeeks: [],
				activeSet: false,
				currentMonth: new Date().getMonth() + 1,
				currentYear: new Date().getFullYear(),
				currentDay: new Date().getDate(),
				selectedMonth: new Date().getMonth() + 1,
				selectedYear: new Date().getFullYear(),
				selectedDay: new Date().getDate(),
				nowDay: new Date().getUTCDate(),
				nowMonth: new Date().getMonth() + 1,
				nowYear: new Date().getFullYear(),
				months: [
					null,
					"January",
					"February",
					"March",
					"April",
					"May",
					"June",
					"July",
					"August",
					"September",
					"October",
					"November",
					"December",
				],
			};
		},
		mounted() {
			this.detectQuery();
		},
		components: {
			chevronRight,
			chevronLeft,
		},
		computed: {
			displayMonth() {
				return this.months[this.currentMonth];
			},
			displayYear() {
				return " " + this.currentYear;
			},
			isCurrentMonth() {
				return (
					this.nowYear == this.currentYear && this.nowMonth == this.currentMonth
				);
			},
		},
		props: {
			navMode: {
				default: "links",
			},
			dateMode: {
				default: "months",
			},
			linkMode: {
				default: "url",
			},
			startUrl: {
				default: "",
			},
		},

		methods: {
			getItems() {
				var self = this;

				this.loading = true;
				this.newWeeks = [];

				if (this.navMode == "pl") {
					var apiUrl = "/api/example-month.json";
				} else {
					var apiUrl =
						"/actions/event-manager/calendar/minical?" +
						"month=" +
						this.currentMonth +
						"&year=" +
						this.currentYear;
				}

				if (typeof cancel == "function") {
					cancel();
				}

				axios
					.get(apiUrl, {
						crossdomain: true,
						cancelToken: new CancelToken(function executor(c) {
							cancel = c;
						}),
					})
					.then((response) => {
						var dates = response.data.data;
						self.meta = response.data.meta;
						var week = [];
						var prevMonth = true;

						for (var i = 0; i < dates.length; i++) {
							if (prevMonth) {
								if (dates[i].date > 1) {
									dates[i]["notCurrentMonth"] = true;
								} else {
									prevMonth = false;
								}
							}

							week.push(dates[i]);

							if (i == dates.length - 1) {
								var dayOffset = 7 - week.length;

								for (var k = 1; k <= dayOffset; k++) {
									week.push({ date: k, notCurrentMonth: true });
								}

								self.newWeeks.push(week);
								week = [];
							}

							if (week.length >= 7) {
								self.newWeeks.push(week);

								week = [];
							}
						}

						this.weeks = this.newWeeks;
						this.loading = false;
					});
			},
			nextMonth() {
				var self = this;
				var month = self.currentMonth;
				var year = self.currentYear;

				if (month == 12) {
					month = 1;
					year++;
				} else {
					month++;
				}

				if (this.dateMode == "months") {
					self.updateUrl(
						year + "-" + month.toString().padStart(2, "0") + "-01",
					);
				} else {
					self.currentMonth = month;
					self.currentYear = year;
				}
			},
			prevMonth() {
				var self = this;
				var month = self.currentMonth;
				var year = self.currentYear;

				if (month == 1) {
					month = 12;
					year--;
				} else {
					month--;
				}

				if (self.dateMode == "months") {
					self.updateUrl(
						year + "-" + month.toString().padStart(2, "0") + "-01",
					);
				} else {
					self.currentMonth = month;
					self.currentYear = year;
				}
			},
			futureDate(d) {
				if (d.date >= this.nowDay) {
					return true;
				} else {
					return false;
				}
			},
			formatDate(year, month, day) {
				return `${year}-${month.toString().padStart(2, "0")}-${day
					.toString()
					.padStart(2, "0")}`;
			},
			isToday(d) {
				let today = `${this.nowDay}${this.nowMonth}${this.nowYear}`;
				let date = `${d.date}${d.month}${d.year}`;
				if (today === date) {
					return true;
				} else {
					return false;
				}
			},
			isActiveDay(d) {
				if (d.year && d.month && d.date) {
					let date = this.formatDate(d.year, d.month, d.date);
					let selectedDate = this.formatDate(
						this.selectedYear,
						this.selectedMonth,
						this.selectedDay,
					);

					if (selectedDate === date) {
						return true;
					} else {
						return false;
					}
				} else {
					return false;
				}
			},
			activeDay(d) {
				let selectedDate = `${this.selectedDay}${this.selectedMonth}${this.selectedYear}`;
				let date = `${d.date}${d.month}${d.year}`;
				if (selectedDate === date) {
					return true;
				} else {
					return false;
				}
			},
			detectQuery() {
				var self = this;

				var date = window.location.pathname.split("/")[3];

				if (typeof date != "undefined" && date.length == 10) {
					self.currentDay = parseInt(date.split("-")[2]);
					self.currentMonth = parseInt(date.split("-")[1]);
					self.currentYear = parseInt(date.split("-")[0]);
					// separate variables necessary to prevent 'active' state persisting across months
					self.selectedDay = parseInt(date.split("-")[2]);
					self.selectedMonth = parseInt(date.split("-")[1]);
					self.selectedYear = parseInt(date.split("-")[0]);
					self.getItems();
				} else {
					self.getItems();
				}
			},
			urlMatchesStartUrl() {
				if (window.location.href === this.startUrl) {
					return true;
				} else {
					return false;
				}
			},
			urlContainsDate() {
				// Checks if the URL contains a date string (yyyy-mm-dd) and returns a boolean value (true if date string exists)
				var urlSegments = window.location.pathname.split("/");
				var dateRegex = /^\d{4}-\d{2}-\d{2}$/;
				var dateInUrl = false;

				for (var i = 0; i < urlSegments.length; i++) {
					if (urlSegments[i].match(dateRegex)) {
						dateInUrl = true;
					}
				}

				return dateInUrl;
			},
			urlDateMatchesDate(date) {
				// Check if the URL partially matches a date string
				// if we get a month and year match when we are showing a whole month view in twig, we will construct a fragment link and scroll to it
				// if we don't match we'll construct a url plus hash
				// btw if there's no date in the url and it's the current month on the minical, we scroll to fragment
				// so we need to check this across minical and twig view
				let dateMatchesMonth = false;
				let dateYMRegex = /^\d{4}-\d{2}/;
				if (date === dateYMRegex) {
					dateMatchesMonth = true;
				}
				return dateMatchesMonth;
			},
			updateUrl(date) {
				//Will update the url with the date argument provided. Works in both links and query mode
				var self = this;

				if (self.navMode == "links") {
					if (this.urlContainsDate()) {
						window.location.href =
							window.location.origin +
							window.location.pathname.replace(
								new RegExp("(.*/)[^/]+$"),
								"$1",
							) +
							date;
					} else {
						window.location.href =
							window.location.origin +
							window.location.pathname.replace(/\/$/, "") +
							"/" +
							date;
					}
				} else if (self.navMode == "query") {
				} else if (self.navMode == "ajax") {
					self.currentMonth = parseInt(date.split("-")[1]);
					self.currentYear = parseInt(date.split("-")[0]);

					self.getItems();
				}
			},
			hashOrLink(day) {
				//switch the day link to a fragment identifier you can scroll to on the page (whole month list)
				// you must construct the id in twig to match eg group instances by day and section id with
				//  id="date-{{date|date('Y-m-d')}}"
				// you must pad the jS month to match twig as that is how it's been decided
				let paddedMonth = day.month.toString().padStart(2, "0");
				let paddedDay = day.date.toString().padStart(2, "0");
				let hash = `#date-${day.year}-${paddedMonth}-${paddedDay}`;
				let link = `${day.link}`;
				let monthOfDay = `${day.year}-${paddedMonth}`;
				let monthLinkAndHash = `${this.startUrl}/${monthOfDay}-01${hash}`;

				if (this.linkMode == "hash") {
					if (this.navMode != "ajax") {
						return hash;
					} else {
						// if we're not reloading on navigation then we need to check if the calendar month is present in the window url. If it is, then we only want to scroll in place, otherwise, we want to navigate to new page
						if (
							this.currentMonth === this.nowMonth &&
							this.urlMatchesStartUrl()
						) {
							// url is /calendar and minical shows this month');
							return hash;
						} else if (
							this.currentMonth != this.nowMonth &&
							this.urlDateMatchesDate(monthOfDay)
						) {
							// url matches the date on minical, and we've paged off start');
							return hash;
						} else {
							return monthLinkAndHash;
						}
					}
				} else {
					return link;
				}
			},
			closeMiniCal: function () {
				// on mobile selecting any date also closes the minical
				let parent = document.getElementById("date-picker");
				parent.classList.toggle("is-active");
			},
			dateLabel(day) {
				return day.date + ", " + this.months[day.month] + " " + day.year;
			},
		},
		watch: {
			currentMonth: function () {
				this.getItems();
			},
		},
	};
</script>
